import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import Button from '../Button';

/**
 * TailwindMuiExample Component
 *
 * Demonstrates how to use Tailwind CSS alongside MUI components
 * without style conflicts or ordering issues.
 *
 * Key patterns:
 * 1. All Tailwind classes use the 'tw:' prefix
 * 2. MUI components work normally without prefixes
 * 3. Styles can be mixed in the same component
 * 4. MUI's sx prop and Tailwind classes coexist
 *
 * @example
 * ```tsx
 * <TailwindMuiExample />
 * ```
 */
export const TailwindMuiExample: React.FC = () => {
  return (
    <Box sx={{ p: 3 }}>
      {/* MUI Typography with standard MUI styling */}
      <Typography variant="h5" gutterBottom>
        Tailwind + MUI Integration Example
      </Typography>

      <Typography variant="body1" sx={{ mb: 3 }}>
        This component demonstrates how Tailwind CSS and Material-UI work
        together seamlessly.
      </Typography>

      {/* MUI Paper component with MUI sx prop */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          MUI-only Styled Section
        </Typography>
        <Typography variant="body2">
          This section uses only Material-UI's styling system via the sx prop.
        </Typography>
      </Paper>

      {/* MUI Paper with Tailwind utility classes */}
      <Paper
        elevation={3}
        className="tw:p-6 tw:mb-6 tw:bg-gradient-to-r tw:from-blue-50 tw:to-indigo-50"
      >
        <div className="tw:mb-4">
          <Typography variant="h6">Tailwind-styled Section</Typography>
        </div>
        <div className="tw:text-gray-700">
          <Typography variant="body2">
            This section uses Tailwind utility classes (with 'tw:' prefix) for
            padding, margins, and background gradients.
          </Typography>
        </div>
      </Paper>

      {/* Mixed MUI and Tailwind styling */}
      <div className="tw:grid tw:grid-cols-1 md:tw:grid-cols-2 tw:gap-4 tw:mb-6">
        <Paper elevation={2} className="tw:p-4">
          <div className="tw:font-bold tw:text-blue-900 tw:mb-2">
            <Typography variant="subtitle1">Card 1: Mixed Styling</Typography>
          </div>
          <Typography variant="body2">
            MUI Paper with Tailwind grid layout, padding, and text utilities.
          </Typography>
        </Paper>

        <Paper elevation={2} className="tw:p-4">
          <div className="tw:font-bold tw:text-tanner-gray-700 tw:mb-2">
            <Typography variant="subtitle1">Card 2: Mixed Styling</Typography>
          </div>
          <Typography variant="body2">
            Demonstrates responsive grid using Tailwind's md: breakpoint.
          </Typography>
        </Paper>
      </div>

      {/* MUI Button (from prism-core) with Tailwind wrapper */}
      <div className="tw:flex tw:gap-4 tw:flex-wrap">
        <Button variant="contained" color="primary">
          MUI Button (Default)
        </Button>

        <div className="tw:transform tw:transition-all tw:duration-300 hover:tw:scale-125 hover:tw:rotate-3 hover:tw:shadow-2xl">
          <Button variant="contained" color="primary">
            MUI Button with Tailwind Hover
          </Button>
        </div>

        <div className="tw:rounded-lg tw:shadow-lg">
          <Button variant="contained">MUI Button in Tailwind Container</Button>
        </div>
      </div>

      {/* Best Practices Section */}
      <Paper elevation={1} sx={{ mt: 4, p: 3, bgcolor: 'grey.100' }}>
        <Typography variant="h6" gutterBottom>
          Best Practices
        </Typography>
        <ul className="tw:list-disc tw:list-inside tw:space-y-2 tw:text-sm tw:text-gray-700">
          <li>
            Always use{' '}
            <code className="tw:bg-gray-200 tw:px-2 tw:py-1 tw:rounded">
              tw:
            </code>{' '}
            prefix for Tailwind classes
          </li>
          <li>
            Apply Tailwind to wrapper divs, not directly on MUI components
            (MUI's emotion styles have higher specificity)
          </li>
          <li>
            Use MUI's{' '}
            <code className="tw:bg-gray-200 tw:px-1 tw:rounded">sx</code> prop
            for styling MUI components directly
          </li>
          <li>
            Use Tailwind for layout utilities (grid, flex, spacing) on wrapper
            elements
          </li>
          <li>
            For forcing Tailwind on MUI, use important modifier:{' '}
            <code className="tw:bg-gray-200 tw:px-1 tw:rounded">
              tw:!text-red-500
            </code>
          </li>
        </ul>
      </Paper>
    </Box>
  );
};

export default TailwindMuiExample;
